import { Component, ErrorInfo, ReactNode } from "react";
import { logError, circuitTripped, computeFingerprint } from "@/lib/errorLogger";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message?: string;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const fp = computeFingerprint({
      errorClass: "react_render_error",
      message: error.message,
      stack: error.stack,
    });
    // Log unless circuit tripped
    if (!circuitTripped(fp)) {
      void logError({
        errorClass: "react_render_error",
        message: error.message,
        stack: error.stack,
        context: { componentStack: info.componentStack?.slice(0, 1500) || "" },
      });
    }
  }

  private handleHome = () => {
    window.location.href = "/dashboard";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-lg w-full border border-gold/30 rounded-lg bg-card p-8 text-center space-y-4"
             style={{ fontFamily: "Georgia, serif" }}>
          <div className="flex justify-center">
            <AlertTriangle className="w-10 h-10 text-gold" />
          </div>
          <h1 className="text-2xl font-semibold">Something went wrong on our end.</h1>
          <p className="text-muted-foreground">
            It has been logged and our team will see it. You can head back to your dashboard
            and continue from there.
          </p>
          <div className="flex justify-center pt-2">
            <Button onClick={this.handleHome}>Back to dashboard</Button>
          </div>
        </div>
      </div>
    );
  }
}

export default GlobalErrorBoundary;
